import getUsersForCompany, {deleteUser} from "API/usersApi";
import {SalvoContext} from "../contexts/SalvoContext";
import ListComponent from "components/ListComponent";
import React, {useContext, useEffect, useRef, useState} from "react";
import {Col, Container, Placeholder, Row} from "react-bootstrap";
import {toast} from "react-toastify";
import messageFromError from "utils/errorUtils";
import ChangeEmailModal from 'components/Modals/ActionModals/ChangeEmailModal';
import ChangePasswordModal from 'components/Modals/ActionModals/ChangePasswordModal';
import ChangeRoleModal from 'components/Modals/ActionModals/ChangeRoleModal';
import ConfirmModal from 'components/Modals/ConfirmModal';
import RegisterUserModal from 'components/Modals/RegisterUserModal';
import {CREATE_USERS, DELETE_USERS, GET_USERS_ALL, UPDATE_USERS} from "../Claims";
import ClaimGuard from "../components/Auth/ClaimGuard";
import {useParams, useSearchParams} from "react-router-dom";
import {useAuth0} from "@auth0/auth0-react";
import BackTextButton from "../components/BackTextButton";
import {getRandomInt, sleep} from "../utils/utils";
import {useTranslation} from "react-i18next";
import MyTable from "../components/core/Components/MyTable";
import ConditionalRender, {Fallback} from "../components/ConditionalRender";
import Menu from "../components/core/Components/Menu/Menu";
import MenuItem from "../components/core/Components/Menu/MenuItem";
import {faEnvelope, faEye, faKey, faTrashCan, faUserShield} from "@fortawesome/free-solid-svg-icons";
import useClaims from "../Hooks/useClaims";

const UsersPage = () => {
    const [users, setUsers] = useState([])

    const [showActionModal, setShowActionModal] = useState(false)
    const [showConfrimDeleteModal, setShowConfrimDeleteModal] = useState(false)
    const [showDeletionSpinner, setShowDeletionSpinner] = useState(false)
    const [showCreateUserModal, setShowCreateUserModal] = useState(false)

    const [showLoadingFetch, setShowLoadingFetch] = useState(false)

    const { user: auth0User  } = useAuth0();
    const {t} = useTranslation()
    
    const modalType = useRef('')
    const selectedUser = useRef()

    const {companyId} = useParams()
    const {hasClaim} = useClaims()
    const { setImpersonatedUser } = useContext(SalvoContext);

    useEffect(() => {
      if(!companyId)
        return;
    
      fetchUsers(0)

    }, [companyId])

    async function fetchUsers(delay = 1200) {
      try {
        setShowLoadingFetch(true)
        await sleep(delay); /* Sleep to allow the Auth0 to propagate changes */
        const response = await getUsersForCompany(companyId)
        setUsers(response.data)
      }
      catch (error) {
          toast.error(messageFromError(error))
      }
      finally {
        setShowLoadingFetch(false)
      }
    }

    async function deleteSelectedUser()
    {
        try {
          setShowDeletionSpinner(true)
          await deleteUser(selectedUser.current.id)
          toast.success("Deleted")
          setShowConfrimDeleteModal(false)
          fetchUsers()
        }
        catch (error) {
          toast.error(messageFromError(error))
        }
        finally
        {
          setShowDeletionSpinner(false)
        }
    }

    function showChangePasswordModal()
    {
      modalType.current = 'Password'
      setShowActionModal(true)
    }

    function showChangeEmailModal()
    {
      modalType.current = 'Email'
      setShowActionModal(true)
    }

    function showChangeRoleModal()
    {
      modalType.current = 'Role'
      setShowActionModal(true)
    }

    const [searchParams] = useSearchParams()

    const UserPlaceholder = ({count = null}) => {
        if(!count)
            count = getRandomInt(2, 6)

        return (
            <>
                {Array(count).fill(count).map((_, index) => (
                    <tr key={index}>
                        <td style={{verticalAlign: 'middle'}}>
                            <Placeholder animation="glow">
                                <Placeholder xs={getRandomInt(2, 8)} className={'rounded'}/>
                            </Placeholder>
                        </td>
                        <td style={{verticalAlign: 'middle'}}>
                            <Placeholder animation="glow">
                                <Placeholder xs={getRandomInt(2, 8)} className={'rounded'}/>
                            </Placeholder>
                        </td>
                        <td style={{verticalAlign: 'middle'}}>
                            <Placeholder animation="glow" className={'d-flex justify-content-center'}>
                                <Placeholder xs={3} className={'rounded'}/>
                            </Placeholder>
                        </td>
                    </tr>
                ))}
            </>
        );
    }

    return (
        <div>
            <Container className="mt-3">
                <Row className="justify-content-center">
                    <Col>
                        {searchParams.get('backTo') && <BackTextButton text='Back to companies' className={'mb-1'}/>}
                        <ListComponent title={t('pages.users.title')} onPlusClicked={() => {
                            setShowCreateUserModal(true)
                        }} plusButtonClaim={CREATE_USERS} useWrapper={false}>
                            <Container fluid className="p-0">
                                <Row>
                                    <Col className="p-0">
                                        <MyTable alignMiddle>
                                            <thead>
                                            <tr>
                                                <th>{t('pages.users.email')}</th>
                                                <th>{t('pages.users.role')}</th>
                                                <ClaimGuard requiredClaim={UPDATE_USERS}>
                                                    <th width='15%' className="text-center">{t('global.operations.manage')}</th>
                                                </ClaimGuard>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <ConditionalRender condition={!showLoadingFetch}>
                                                <Fallback>
                                                    <UserPlaceholder/>
                                                </Fallback>


                                {users.map(user => {
                                    return (
                                        <tr key={user.id}>
                                            <td>
                                            <span>{user.email}</span>
                                            </td>
                                            <td>
                                                <span>{user.userRoles.length > 0 ? user.userRoles[0].name : "-"}</span>
                                            </td>
                                            <ClaimGuard requiredClaim={UPDATE_USERS}>
                                                <td>
                                                    <div className="d-flex justify-content-center">
                                                            <Menu placement={'left'}>
                                                                {hasClaim(GET_USERS_ALL) && auth0User.sub !== user.id &&
                                                                    <MenuItem text={t('pages.users.menu.viewAsUser')} icon={faEye} variant="info" onClick={() => {
                                                                        setImpersonatedUser(user);
                                                                    }}/>
                                                                }
                                                                <MenuItem text={t('pages.users.menu.changeEmail')} icon={faEnvelope} onClick={() => { selectedUser.current = user; showChangeEmailModal(); }}/>
                                                                <MenuItem text={t('pages.users.menu.changePassword')} icon={faKey} onClick={() => { selectedUser.current = user; showChangePasswordModal(); }} />
                                                                <MenuItem text={t('pages.users.menu.changeRole')} icon={faUserShield} onClick={() => { selectedUser.current = user; showChangeRoleModal(); }}/>
                                                                {hasClaim(DELETE_USERS) && auth0User.sub !== user.id &&
                                                                    <MenuItem text={t('global.operations.delete')} icon={faTrashCan} variant={'danger'} onClick={() => { selectedUser.current = user; setShowConfrimDeleteModal(true); }}/>
                                                                }
                                                            </Menu>
                                                    </div>
                                                </td>
                                            </ClaimGuard>
                                        </tr>
                                    )
                                })}
                            </ConditionalRender>
                          </tbody>
                      </MyTable>
                    </Col>
                  </Row>
                </Container>
              </ListComponent>
            </Col>
          </Row>
        </Container>
        <RegisterUserModal companyId={companyId} show={showCreateUserModal} onDismiss={() => {setShowCreateUserModal(false)}} onRegistered={fetchUsers}/>

        {selectedUser.current && <ConfirmModal active={showConfrimDeleteModal} 
          onCancel={() => setShowConfrimDeleteModal(false)}
          onConfirm={deleteSelectedUser}
          showSpinner={showDeletionSpinner}
          title={t('dialogs.confirmDeleteDialog.title')}
          positiveButton= {
            {
              text: t('global.operations.delete'),
              variant: "danger"
            }
          }>

          <span>{t('dialogs.confirmDeleteDialog.youAreAboutTo')} <strong style={{color: 'red'}}>{t('dialogs.confirmDeleteDialog.delete')}</strong> <strong>{selectedUser.current.email}</strong>. {t('dialogs.confirmDeleteDialog.areYouSure')}</span>

        </ConfirmModal>}
        

        {/* MODALS */}
        {modalType.current === 'Email' && <ChangeEmailModal show={showActionModal} onHide={() => {setShowActionModal(false)}} userId={selectedUser.current.id} onUpdate={fetchUsers}/>}
        {modalType.current === 'Password' && <ChangePasswordModal show={showActionModal} onHide={() => {setShowActionModal(false)}} userId={selectedUser.current.id} onUpdate={fetchUsers}/>}
        {modalType.current === 'Role' && <ChangeRoleModal show={showActionModal} onHide={() => {setShowActionModal(false)}} user={selectedUser.current} onUpdate={fetchUsers}/>}

      </div>
    );
};

export default UsersPage;